import { useCallback, useEffect, useRef, useState } from 'react';
import { armDeadline, isExpired, progressLeft, remainingMs, type Deadline } from './autoCloseDeadline';

const TICK_MS = 250;

/**
 * Deadline-based auto-close. The old card decremented a counter from a 1 s
 * timeout that the parent's every re-render restarted — and the room
 * re-renders at least once a second, so the countdown stalled. Here the
 * deadline is an instant; the interval only reads it, and `onClose` lives in
 * a ref so the parent's inline callbacks never restart anything.
 *
 * Not armed while the tab is hidden; armed afresh when it becomes visible.
 */
export function useAutoClose({ active, durationMs, onClose }: { active: boolean; durationMs: number; onClose: () => void }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [deadline, setDeadline] = useState<Deadline | null>(null);
  const [progress, setProgress] = useState(1);
  const [remaining, setRemaining] = useState(durationMs);

  const arm = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      setDeadline(null);
      return;
    }
    setDeadline(armDeadline(Date.now(), durationMs));
    setProgress(1);
    setRemaining(durationMs);
  }, [durationMs]);

  useEffect(() => {
    if (!active) {
      setDeadline(null);
      setProgress(1);
      setRemaining(durationMs);
      return undefined;
    }
    arm();
    const onVisibility = () => arm();
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [active, arm, durationMs]);

  useEffect(() => {
    if (!deadline) return undefined;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setProgress(progressLeft(deadline, now));
      setRemaining(remainingMs(deadline, now));
      if (isExpired(deadline, now)) {
        setDeadline(null);
        onCloseRef.current();
      }
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [deadline]);

  return { progress: active && deadline ? progress : 1, remainingMs: active && deadline ? remaining : durationMs, rearm: arm };
}
